import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { cn } from '../../lib/cn';

/**
 * Button — variants + sizes match Agent Builder's canonical button colors
 * verbatim (sampled from WorkflowManager.jsx + ui/Modal.jsx):
 *
 *   primary     bg-primary-600 hover:bg-primary-500   (page CTA — "New X")
 *   success     bg-emerald-500 hover:bg-emerald-600 (modal Save/Create)
 *   secondary   bg-blue-500    hover:bg-blue-600    (modal alt action)
 *   destructive bg-red-500     hover:bg-red-600     (delete)
 *   ghost       border-only                         (cancel/outline)
 *
 * NO tinted shadows + NO active-state colors — AB's softer feel comes from
 * leaving those off; adding them makes buttons look "deep/sharp".
 */
export type ButtonVariant = 'primary' | 'success' | 'secondary' | 'destructive' | 'ghost';
export type ButtonSize = 'sm' | 'md' | 'lg';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
    variant?: ButtonVariant;
    size?: ButtonSize;
    leftIcon?: ReactNode;
    rightIcon?: ReactNode;
    fullWidth?: boolean;
    isDarkMode?: boolean; // only used by ghost variant — others are dark-agnostic
}

// Color ladders match IGNODE main app (ignode-web-ui/ignode_ui) verbatim.
// Primary brand color is `primary-*` (sky-blue palette extended in
// tailwind.config.js to match IGNODE's `primary` palette exactly).
//
//   primary     bg-primary-600 hover:bg-primary-700 text-white shadow
//                  (IGNODE canonical CTA — used in MLTrainingPanel,
//                   DashboardsList, ProcessAlertModal, etc.)
//   success     bg-emerald-500 hover:bg-emerald-600 (Save/Create modal)
//   secondary   bg-primary-500 hover:bg-primary-600 (lighter alt)
//   destructive bg-red-600 hover:bg-red-700        (IGNODE: 600→700
//                  hover-DARKER, NOT 500→600 like AB's modal red)
//   ghost       border-only, gray-300 / gray-600   (IGNODE light/dark)
//
// IGNODE's hover direction is 600→700 (DARKER on hover) — opposite of
// Agent Builder's 600→500 (LIGHTER). User asked for IGNODE match this
// turn; switched.
const VARIANT: Record<ButtonVariant, (isDark: boolean) => string> = {
    primary: () =>
        'bg-primary-600 hover:bg-primary-700 text-white shadow',
    success: () =>
        'bg-emerald-500 hover:bg-emerald-600 text-white',
    secondary: () =>
        'bg-primary-500 hover:bg-primary-600 text-white',
    destructive: () =>
        'bg-red-600 hover:bg-red-700 text-white',
    ghost: (isDark) =>
        isDark
            ? 'border border-gray-600 text-gray-300 hover:bg-gray-700'
            : 'border border-gray-300 text-gray-700 hover:bg-gray-100',
};

const SIZE: Record<ButtonSize, string> = {
    sm: 'px-3 py-1.5 text-[12px] rounded-md gap-1.5',
    md: 'px-4 py-2 text-[13px] rounded-lg gap-2',
    lg: 'px-6 py-3 text-sm rounded-lg gap-2',
};

const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
    {
        variant = 'primary',
        size = 'md',
        leftIcon,
        rightIcon,
        fullWidth,
        isDarkMode = false,
        className,
        disabled,
        children,
        type,
        ...rest
    },
    ref,
) {
    return (
        <button
            ref={ref}
            type={type ?? 'button'}
            disabled={disabled}
            className={cn(
                'inline-flex items-center justify-center font-semibold whitespace-nowrap',
                'transition-colors duration-150',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2',
                'disabled:opacity-50 disabled:cursor-not-allowed',
                VARIANT[variant](isDarkMode),
                SIZE[size],
                fullWidth && 'w-full',
                className,
            )}
            {...rest}
        >
            {leftIcon && <span className="inline-flex shrink-0">{leftIcon}</span>}
            {children}
            {rightIcon && <span className="inline-flex shrink-0">{rightIcon}</span>}
        </button>
    );
});

export default Button;
